<template>
  <RfSelect
    v-model="selected"
    multiple
    extended
    class="w-full"
    preset="modalMenu"
    variant="outlined"
    :visibleCount="5"
    title="Who are you targeting"
    placeholder="Select one or more segments"
    :items="segmentListWithHeaders"
    itemKey="id"
    itemValue="name"
    returnObject
    search="Search segments..."
    sameWidth
    clearable
  >
    <template #item="{ item, index }">
      <template
        v-for="(displayFilters, _) in [getDisplayFiltersForSegment(item.rawValue)]"
        :key="_"
      >
        <span class="inline-flex w-full items-center justify-between gap-2">
          {{ item.name }}
          <button
            v-if="displayFilters.length"
            :ref="`tooltipAnchorEl-${index}`"
            class="inline-flex"
            @focus="$refs?.[`tooltipEl-${index}`]?.[0]?.show?.()"
            @mouseenter="$refs?.[`tooltipEl-${index}`]?.[0]?.show?.()"
            @blur="$refs?.[`tooltipEl-${index}`]?.[0]?.hide?.()"
            @mouseleave="$refs?.[`tooltipEl-${index}`]?.[0]?.hide?.()"
          >
            <RfSeeMoreIcon
              class="rf-row--see-more bg-input-background z-10 h-6! w-6! rounded-full"
            />
            <RfTooltip
              :ref="`tooltipEl-${index}`"
              :anchor="$refs?.[`tooltipAnchorEl-${index}`]?.[0]"
              class="mt-2 w-fit"
            >
              <template #tooltip>
                <div
                  v-for="(filter, filterIndex) in displayFilters"
                  :key="`${filter[0]}-${filter[1]}-${filterIndex}`"
                  class="text-body text-grey-1 w-64"
                >
                  {{ filter[0] }}{{ filter[1] ? ":" : "" }}
                  <span class="text-black-2">{{ filter[1] }}</span>
                </div>
              </template>
            </RfTooltip>
          </button>
        </span>
      </template>
    </template>
  </RfSelect>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useAllSegmentsStore } from "@/pinia/allSegmentsStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import { useSegmentFilters } from "@/utils/composables/useSegmentFilters";
import RfSelect from "@/components/inputs/RfSelect.vue";
import RfSeeMoreIcon from "@/components/icons/RfSeeMoreIcon.vue";
import RfTooltip from "@/components/tooltip/RfTooltip.vue";

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  isPush: { type: Boolean, default: false },
});
const emit = defineEmits(["update:modelValue"]);

const route = useRoute();
const allSegmentsStore = useAllSegmentsStore();
const appsStore = useAppsStore();
const userStore = useUserStore();
const { getDisplayFiltersForSegment } = useSegmentFilters();

const isSandboxLocked = computed(
  () => !!appsStore.app?.flags?.sandbox && !userStore.isCurrentSuperAdmin,
);

const testUserSegment = computed(
  () => allSegmentsStore.segments.find(s => s.segment_type === "system_test_users") ?? null,
);

const segmentListWithHeaders = computed(() => {
  const isScheduledPush = props.isPush;
  return (allSegmentsStore.segments || [])
    .filter(segment => {
      if (isScheduledPush) return segment.segment_type === "pinpoint";
      return !isSandboxLocked.value || segment.segment_type === "system_test_users";
    })
    .map(segment => ({ name: segment.name, id: segment.id, rawValue: segment }));
});

const selected = ref([...props.modelValue]);

const idsOf = list => (list || []).map(s => s?.id ?? s);

// Registered before the constraint watchers so a sandbox-forced selection made
// during setup still emits the normalized value through v-model.
watch(
  selected,
  val => {
    const normalized = val
      .map(s => {
        const matched = allSegmentsStore.segments.find(seg => seg.id === (s?.id ?? s));
        return matched ? { id: matched.id, name: matched.name } : null;
      })
      .filter(Boolean);
    emit("update:modelValue", normalized);
  },
  { deep: true },
);

watch(
  () => props.modelValue,
  val => {
    const incoming = idsOf(val);
    const current = idsOf(selected.value);
    if (incoming.length === current.length && incoming.every((id, i) => id === current[i])) {
      return;
    }
    selected.value = [...(val || [])];
    applyConstraints();
  },
);

const applyConstraints = (testSeg = testUserSegment.value) => {
  if (!isSandboxLocked.value) return;
  if (props.isPush) return;
  selected.value = selected.value.filter(s => {
    const id = s?.id ?? s;
    return (
      allSegmentsStore.segments.find(seg => seg.id === id)?.segment_type === "system_test_users"
    );
  });
  if (!selected.value.length && testSeg?.id) {
    selected.value = [testSeg];
  }
};

watch(testUserSegment, seg => applyConstraints(seg), { immediate: true });
watch(isSandboxLocked, locked => {
  if (locked) applyConstraints();
});

if (!allSegmentsStore.segments.length) {
  allSegmentsStore.getSegments({ appId: route.params.aid });
}
</script>
